import React from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import SolutionsFooter from './SolutionsFooter';
import useSEO from '../hooks/useSEO';

function FeaturesHub({ logo }) {
  useSEO({
    title: 'Product Features',
    description: 'Explore Veydra product features for model design, experimentation, decision intelligence, and enterprise integrations.',
  });

  const features = [
    {
      title: 'AI-Assisted Model Design',
      description: 'Use guided AI workflows to build and refine dynamic simulation models faster.',
      href: '/features/ai-assistant',
    },
    {
      title: 'Scenario Experimentation',
      description: 'Test policy and resource choices under uncertainty before committing in production.',
      href: '/features/scenario-experimentation',
    },
    {
      title: 'Decision Intelligence Dashboards',
      description: 'Connect model outputs to executive-ready views that explain why outcomes change over time.',
      href: '/features/decision-intelligence',
    },
    {
      title: 'MCP + Agent Integrations',
      description: 'Enable AI agents to reason over simulation context with MCP-ready architecture.',
      href: '/features/mcp-integration',
    },
    {
      title: 'Decision-Ready Data Layer',
      description: 'Connect governed enterprise data to simulation and preserve an auditable trail to insight.',
      href: '/features/data-layer',
    },
    {
      title: 'Code-First Methodology',
      description: 'See how executable, version-controlled models create AI-native digital twins and keep strategy aligned with live operations.',
      href: '/papers/code-first-methodology',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <TopNav logo={logo} />

      <main className="max-w-5xl mx-auto px-8 py-16 md:py-24">
        <div className="">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Platform Capabilities</p>
          <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">
            Features built for high-stakes decision-making
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-3xl">
            From model creation to scenario analysis and enterprise integration, Veydra gives teams the tools to test decisions before they are deployed.
          </p>
        </div>

          <div className="max-w-5xl mx-auto mt-14">
            <div className="bg-white border border-gray-200 rounded-lg p-0 overflow-hidden">
              <img
                src="/preview-scenarios.png"
                alt="Scenario comparison view showing policy and market shock outcomes"
                className="w-full h-auto border border-black"
              />
            </div>
            <p className="mt-3 text-sm text-gray-500 text-center">
              Scenario navigation: compare policy choices, market shocks, and AI adoption paths before you commit.
            </p>
          </div>
      </main>

      <section className="bg-gray-50 py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-8">

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-left">
              <div className="w-10 h-10 bg-gray-900 rounded flex items-center justify-center mb-5">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-3">Model the Logic</h3>
              <p className="text-gray-600 leading-relaxed">
                Map the feedback loops and hidden interdependencies that actually drive your organization.
              </p>
            </div>

            <div className="text-left">
              <div className="w-10 h-10 bg-gray-900 rounded flex items-center justify-center mb-5">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-3">Stress-Test Scenarios</h3>
              <p className="text-gray-600 leading-relaxed">
                Move beyond spreadsheets. Toggle variables in real-time to see ripple effects before you commit capital.
              </p>
            </div>

            <div className="text-left">
              <div className="w-10 h-10 bg-gray-900 rounded flex items-center justify-center mb-5">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-3">Align the Team</h3>
              <p className="text-gray-600 leading-relaxed">
                Turn static reports into a "Decision Sandbox." Help stakeholders visualize trade-offs and ROI together.
              </p>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-8 py-14 md:py-16">

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {features.map((feature) => (
            <Link
              key={feature.href}
              to={feature.href}
              className="block border border-gray-200 rounded-lg p-6 hover:border-gray-400 transition-colors"
            >
              <h2 className="text-xl font-medium text-gray-900 mb-3">{feature.title}</h2>
              <p className="text-gray-600 leading-relaxed mb-4">{feature.description}</p>
              <span className="text-sm font-medium text-gray-900">Explore feature →</span>
            </Link>
          ))}
        </div>
      </main>

      <SolutionsFooter />
    </div>
  );
}

export default FeaturesHub;
